import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

/**
 * Settings -> System: the thresholds behind the CPU-pressure banner, plus the readout that
 * lets a "delayed" claim be corroborated rather than taken on faith (G4.8, G4.9, G4.11).
 *
 * WHY THE BACKLOG READOUT IS HERE AND NOT IN THE HEADER. The banner has to be small and
 * unambiguous; depth belongs where someone has gone looking for it. Publishing backlog
 * depth at all is a `docs/BACKLOG.md` B1 prerequisite and this partially discharges it.
 *
 * THE UNREADABLE CONDITION IS SHOWN ONLY HERE (G4.10). If the cgroup CPU counter cannot be
 * read, the header shows NOTHING — an unreadable signal is not evidence of pressure — but
 * silently swallowing it would leave an operator unable to tell a healthy app from a blind
 * one. So it is stated on this page, in full, with the paths that failed. Same reasoning
 * as `inbound_pending_durable_readable`, whose value is shown below for the same reason:
 * a failed S3 listing and an empty pipeline otherwise look identical.
 *
 * THE BOUNDS COME FROM THE SERVER, not from constants duplicated here, so the numbers this
 * form advertises cannot drift out of agreement with the validation that enforces them.
 */

const POLL_MS = 10_000

interface AppConfig {
  inbound_match_window_hours: number
  cpu_pressure_enabled: boolean
  cpu_pressure_threshold_pct: number
  cpu_pressure_window_seconds: number
  inbound_match_window_max_hours: number
  inbound_retention_hours: number
  cpu_pressure_window_min_seconds: number
  cpu_pressure_window_max_seconds: number
}

interface Pressure {
  average_pct: number | null
  threshold_pct: number
  window_seconds: number
  configured_window_seconds: number
  sample_count: number
  readable: boolean
  unreadable_reason: string | null
  quota_cores: number
  over_threshold: boolean
  enabled: boolean
  sample_interval_seconds: number
  backlog: {
    scope_note: string
    ocr_backlog: number
    inbound_backlog: number
    quality_backlog: number
    inbound_pending_durable: number | null
    inbound_pending_durable_readable: boolean | null
    cpu_budget: number | null
    gate_started: boolean
    admissions?: number | null
    in_flight?: number | null
    waiting?: number | null
    peak_in_flight?: number | null
  }
}

/**
 * U26 — the mailcow readout, and why it is a STATE rather than a boolean.
 *
 * The infrastructure for this feature was wired and verified in the running task definition,
 * and this page still said it was off. It said so because this card used to be static prose
 * whose first sentence was "Off unless MAILCOW_PROVISIONING_ENABLED is set" — true as prose,
 * unfalsifiable as a reading, and identical whatever the container carries.
 *
 * A tick would not have fixed it. Three unlike situations all look like "disabled", and they
 * need three different operator actions: the switch is off; the switch is on and settings are
 * absent; the switch is on and the stored key will not decrypt under the deployed SECRET_KEY —
 * that last one is invisible in a task definition, because the value is present, the right
 * length, and wrong.
 *
 * `configured` NEVER MEANS "working". Resolving the configuration contacts nothing, so
 * claiming reachability here would be the same defect in the other direction.
 */
type MailcowState = 'disabled' | 'not_configured' | 'key_unreadable' | 'configured'

interface MailcowStatus {
  state: MailcowState
  detail: string
  enabled: boolean
  api_base: string
  mailbox_domain: string
  default_quota_mib: number
  key_present: boolean
  key_readable: boolean
  key_error: string | null
  missing: string[]
  provisioning_invoked: boolean
  note: string | null
  privilege_warning: string
}

const MAILCOW_STATE_LABEL: Record<MailcowState, string> = {
  disabled: 'Switched off',
  not_configured: 'Switched on — settings missing',
  key_unreadable: 'Switched on — the stored key will not decrypt here',
  configured: 'Configured (reachability not asserted)',
}

const card: React.CSSProperties = {
  background: theme.bgAlt,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '1.2rem',
  marginBottom: '1.5rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.6rem',
  background: theme.bg,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: '0.9rem',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  color: theme.textMuted,
  marginBottom: '0.2rem',
}

function Readout({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.35rem 0' }}>
      <span style={{ color: theme.textMuted, fontSize: '0.85rem' }} title={hint}>
        {label}
        {hint ? <span style={{ cursor: 'help' }}> ⓘ</span> : null}
      </span>
      <span style={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  )
}

export default function SystemSettings() {
  const isAdmin = useIsAdmin()
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [form, setForm] = useState<Partial<AppConfig>>({})
  const [pressure, setPressure] = useState<Pressure | null>(null)
  const [mailcow, setMailcow] = useState<MailcowStatus | null>(null)
  const [mailcowError, setMailcowError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/settings/app-config', { credentials: 'same-origin' })
      if (!res.ok) {
        setError(res.status === 403 ? null : 'Could not read system settings')
        return
      }
      const body: AppConfig = await res.json()
      setConfig(body)
      setForm(body)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [])

  // Read ONCE, not polled: these are environment configuration, fixed for the life of the
  // task definition. Polling them would suggest they can change under the operator's feet.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/settings/mailcow', { credentials: 'same-origin' })
        if (!res.ok) {
          // 403 is a viewer, not a fault: the card is inside AdminOnly anyway.
          if (!cancelled && res.status !== 403) {
            setMailcowError('Could not read the mailcow provisioning state')
          }
          return
        }
        const body: MailcowStatus = await res.json()
        if (!cancelled) setMailcow(body)
      } catch {
        if (!cancelled) setMailcowError('Could not read the mailcow provisioning state')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const res = await fetch('/api/system/pressure', { credentials: 'same-origin' })
        if (!res.ok) return
        const body: Pressure = await res.json()
        if (!cancelled) setPressure(body)
      } catch {
        /* a monitoring read that fails leaves the previous reading visible */
      }
    }
    poll()
    const timer = window.setInterval(poll, POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const res = await fetch('/api/settings/app-config', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cpu_pressure_enabled: form.cpu_pressure_enabled,
          cpu_pressure_threshold_pct: form.cpu_pressure_threshold_pct,
          cpu_pressure_window_seconds: form.cpu_pressure_window_seconds,
          inbound_match_window_hours: form.inbound_match_window_hours,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        const detail = body?.detail
        setError(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: any) => d?.msg ?? String(d)).join('; ')
              : 'Could not save system settings',
        )
        return
      }
      const body: AppConfig = await res.json()
      setConfig(body)
      setForm(body)
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div>Loading…</div>
  if (!isAdmin || !config) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>System</h2>
        <AdminOnly what="System settings" />
      </div>
    )
  }

  const cpuUnreadable = pressure !== null && !pressure.readable
  const durableUnreadable =
    pressure !== null && pressure.backlog.inbound_pending_durable_readable === false

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ margin: 0 }}>System</h2>
      <p style={{ margin: '0.25rem 0 1.5rem', color: theme.textMuted, fontSize: '0.85rem' }}>
        When e2e_scheduler&rsquo;s own CPU is saturated, fax processing here falls behind. This
        page controls the header warning that says so, and shows the backlog numbers that
        corroborate it. CPU is measured from this container&rsquo;s own cgroup quota, not from
        CloudWatch.
      </p>

      {error && (
        <div
          style={{
            background: '#3a1e1e',
            border: '1px solid #5a2a2a',
            color: '#ef9a9a',
            padding: '0.75rem',
            borderRadius: 4,
            marginBottom: '1rem',
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {cpuUnreadable && (
        <div
          style={{
            background: '#3a3016',
            border: '1px solid #6b5a26',
            color: '#ffe0b2',
            padding: '0.75rem',
            borderRadius: 4,
            marginBottom: '1rem',
            fontSize: '0.85rem',
            lineHeight: 1.5,
          }}
        >
          <strong>The CPU signal is unreadable on this worker.</strong> The header warning is
          therefore showing nothing at all — an unreadable signal is not evidence of pressure,
          so it must not render as one. It is reported here instead of being swallowed, because
          a blind indicator and a healthy app otherwise look identical.
          {pressure?.unreadable_reason ? (
            <div style={{ marginTop: '0.4rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>
              {pressure.unreadable_reason}
            </div>
          ) : null}
        </div>
      )}

      <form onSubmit={save} style={card}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>CPU pressure warning</h3>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
          <input
            type="checkbox"
            checked={form.cpu_pressure_enabled ?? true}
            onChange={e => setForm(f => ({ ...f, cpu_pressure_enabled: e.target.checked }))}
          />
          Show the header warning when processing is delayed
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.9rem' }}>
          <div>
            <label style={labelStyle}>Threshold (% of this container&rsquo;s CPU quota)</label>
            <input
              type="number"
              min={1}
              max={100}
              value={form.cpu_pressure_threshold_pct ?? 95}
              onChange={e => setForm(f => ({ ...f, cpu_pressure_threshold_pct: Number(e.target.value) }))}
              style={inputStyle}
            />
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.25rem' }}>
              Fires strictly above this value, so exactly {form.cpu_pressure_threshold_pct ?? 95}% does
              not warn.
            </div>
          </div>
          <div>
            <label style={labelStyle}>Averaging window (seconds)</label>
            <input
              type="number"
              min={config.cpu_pressure_window_min_seconds}
              max={config.cpu_pressure_window_max_seconds}
              value={form.cpu_pressure_window_seconds ?? 120}
              onChange={e => setForm(f => ({ ...f, cpu_pressure_window_seconds: Number(e.target.value) }))}
              style={inputStyle}
            />
            <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.25rem' }}>
              {config.cpu_pressure_window_min_seconds}–{config.cpu_pressure_window_max_seconds}s.
              Sampled every {pressure?.sample_interval_seconds ?? 10}s; the average is kept
              server-side so every tab and both workers report the same figure.
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '0.9rem' }}>
          <label style={labelStyle}>Inbound matching window (hours)</label>
          <input
            type="number"
            min={1}
            max={config.inbound_match_window_max_hours}
            value={form.inbound_match_window_hours ?? 24}
            onChange={e => setForm(f => ({ ...f, inbound_match_window_hours: Number(e.target.value) }))}
            style={{ ...inputStyle, maxWidth: 220 }}
          />
          <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Measured per item from that item&rsquo;s own submission, never batch-wide. Refused above{' '}
            {config.inbound_retention_hours} hours, because the window may not outlive the stored
            fax payloads it needs in order to match.
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              background: theme.primary,
              color: theme.onPrimary,
              border: 'none',
              borderRadius: 4,
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && (
            <span style={{ fontSize: '0.82rem', color: theme.textMuted }}>
              Saved. Applies within {Math.round(POLL_MS / 1000)}s in every worker — no redeploy.
            </span>
          )}
        </div>
      </form>

      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Current reading</h3>
        {pressure === null ? (
          <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>Reading…</div>
        ) : (
          <>
            <Readout
              label="CPU, averaged over the window"
              value={
                pressure.average_pct === null
                  ? 'not yet established'
                  : `${pressure.average_pct}% of quota`
              }
              hint="Δcgroup CPU usage ÷ (Δwall time × quota cores). Not clamped at 100%: a short window can legitimately overrun quota before CFS throttles, and clamping would understate our own delay."
            />
            <Readout
              label="Samples in the window"
              value={`${pressure.sample_count} × ${pressure.sample_interval_seconds}s`}
              hint="The average is suppressed until the samples cover at least half the window, so a freshly started worker does not warn off a single OCR page."
            />
            <Readout label="Effective window" value={`${pressure.window_seconds}s`} />
            <Readout label="cgroup quota" value={`${pressure.quota_cores} core(s)`} />
            <Readout
              label="CPU signal readable"
              value={pressure.readable ? 'yes' : 'no — header shows nothing'}
            />
            <Readout
              label="Warning currently showing"
              value={
                pressure.over_threshold
                  ? 'yes'
                  : pressure.enabled
                    ? 'no'
                    : 'no — disabled in settings'
              }
            />
          </>
        )}
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Backlog, for corroboration</h3>
        {pressure === null ? (
          <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>Reading…</div>
        ) : (
          <>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: theme.textMuted, lineHeight: 1.5 }}>
              {pressure.backlog.scope_note}
            </p>
            <Readout label="Awaiting OCR (this worker)" value={pressure.backlog.ocr_backlog} />
            <Readout label="Awaiting download (this worker)" value={pressure.backlog.inbound_backlog} />
            <Readout label="Awaiting quality scoring (this worker)" value={pressure.backlog.quality_backlog} />
            <Readout
              label="Pending inbound, task-wide"
              value={
                pressure.backlog.inbound_pending_durable === null
                  ? 'not established'
                  : durableUnreadable
                    ? `${pressure.backlog.inbound_pending_durable} (a FLOOR — a prefix could not be listed)`
                    : pressure.backlog.inbound_pending_durable
              }
              hint="Counted from the durable pending sidecars in S3, so it is task-wide rather than per-worker. When the readable flag is 0 the count is a floor, not a count — a failed listing and an empty pipeline would otherwise look identical."
            />
            <Readout label="CPU admission budget" value={pressure.backlog.cpu_budget ?? 'unknown'} />
            {pressure.backlog.gate_started ? (
              <>
                <Readout label="Admissions granted (this worker)" value={pressure.backlog.admissions ?? '—'} />
                <Readout label="In flight / waiting (this worker)" value={`${pressure.backlog.in_flight ?? '—'} / ${pressure.backlog.waiting ?? '—'}`} />
                <Readout label="Peak in flight (this worker)" value={pressure.backlog.peak_in_flight ?? '—'} />
              </>
            ) : (
              <Readout
                label="Admission gate"
                value="not started in this worker"
                hint="Read rather than requested: asking for the gate would create it, and a monitoring page must not be the thing that instantiates what it monitors."
              />
            )}
          </>
        )}
      </div>

      {/*
        G19.6 / T11.15 — mailbox provisioning, and the honest statement of what its key is.

        READ-ONLY ON PURPOSE. These values are a deployment's identity — which mail server,
        which domain — set once when the key is created and changed only when it is rotated,
        so they are environment configuration rather than something to tune from a web page.
        What this panel owes an operator is the DISCLOSURE and the READING, not an edit box.

        U26 — THE READING IS THE POINT OF THIS CARD. Everything here used to be static prose,
        so after the settings were wired into the task definition and verified present, this
        page went on describing the default and the operator reasonably concluded the feature
        was off. The state, the host, the domain and whether the stored key decrypts all come
        from `GET /api/settings/mailcow` — a value that can disagree with this deployment
        cannot be written into this file.

        WAVE A ITEM 14 — THE PROSE WAS REMOVED FROM THIS SURFACE; THE FACTS WERE NOT LOST.
        The panel is now five readouts and nothing else. Everything the paragraphs used to
        say is stated in `frontend/src/help/content.ts` under "Mailbox provisioning (mailcow)"
        and "Provisioning is complete only after an IMAP login", which sit OUTSIDE the admin
        gate, and `tests/test_mailcow_settings_surface.py` compares that Help entry against
        `backend/services/mailcow.py`'s API_KEY_PRIVILEGE_WARNING sentence by sentence, failing
        in BOTH directions. In summary, so this file is not silent about what it dropped:

          - provisioning is off unless MAILCOW_PROVISIONING_ENABLED is set, and with it off you
            supply credentials for a mailbox you created yourself;
          - with it on, an account is not complete until a real IMAP login against the mailbox
            succeeds — the login is the authority, not mailcow's API response;
          - the API key is necessarily a FULL mail-server administrator credential, pinned only
            by allow_from with skip_ip_check at 0, set in mailcow's own admin UI, which this
            application can neither read nor set.

        WHAT MUST NOT BE TRIMMED FURTHER: the four states have to stay tellable apart. Every
        state renders the State readout, so no state renders an empty region — rendering
        nothing in each unavailable state WAS the defect fixed in PR #96, reported as the panel
        being "completely missing from the UI". MAILCOW_STATE_LABEL carries one distinct
        sentence per state and "Stored API key" independently distinguishes key_unreadable.
      */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Mailbox provisioning (mailcow)</h3>

        {mailcowError ? (
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: theme.textMuted }}>
            {mailcowError}
          </p>
        ) : null}

        {mailcow ? (
          <div>
            <Readout
              label="State"
              value={MAILCOW_STATE_LABEL[mailcow.state]}
              hint="Four states, not a boolean: off, on-with-settings-missing, on-with-a-key-that-will-not-decrypt, and configured. The three that are not 'configured' need three different actions and all used to read as 'disabled'."
            />
            <Readout label="Mail host" value={mailcow.api_base || 'not set'} />
            <Readout label="Mailbox domain" value={mailcow.mailbox_domain || 'not set'} />
            <Readout label="Default quota (MiB)" value={mailcow.default_quota_mib} />
            <Readout
              label="Stored API key"
              value={
                !mailcow.key_present
                  ? 'none stored'
                  : mailcow.key_readable
                    ? 'stored, decrypts here'
                    : 'stored, WILL NOT DECRYPT here'
              }
              hint="Presence and readability only. Neither the key nor its ciphertext, length or prefix is ever sent to this page. This is necessarily a full mail-server administrator credential — see Help."
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
